"use client";

import React, { useState } from "react";
import { Button } from "@/components/atoms/button";
import { Input } from "@/components/atoms/input";
import { BackgroundPattern } from "@/components/atoms/BackgroundPattern";
import { trackFormSubmissionGTM } from "@/lib/analytics";
import { useHydrated } from "@/lib/hooks/useHydrated";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Loader2, CheckCircle } from "lucide-react";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
type FormState = 'idle' | 'submitting' | 'success' | 'error';

/**
 * FooterNewsletter Component
 *
 * A premium, modern newsletter signup section for the footer.
 * Features:
 * - Glassmorphism input field with backdrop blur and transparency.
 * - Dynamic radial gradient background with brand watermark.
 *
 * @component
 */
export const FooterNewsletter = () => {
  const [formState, setFormState] = useState<FormState>('idle');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const isHydrated = useHydrated();
  const {
    executeRecaptcha
  } = useGoogleReCaptcha();
  const validateEmail = (email_0: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email_0);
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmedCompany = company.trim();
    if (!trimmedCompany) {
      setErrorMessage('Company Name is required');
      setFormState('error');
      return;
    }
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrorMessage('Email is required');
      setFormState('error');
      return;
    }
    if (!validateEmail(trimmedEmail)) {
      setErrorMessage('Please enter a valid email address');
      setFormState('error');
      return;
    }
    setFormState('submitting');
    setErrorMessage('');
    try {
      // Get reCAPTCHA token if available
      let recaptchaToken: string | undefined;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('newsletter_signup');
        } catch (recaptchaError) {
          console.warn('reCAPTCHA token generation failed:', recaptchaError);
        }
      }
      const response = await fetch('/api/forms/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          formType: 'newsletter',
          data: {
            email: trimmedEmail,
            company: trimmedCompany
          },
          recaptchaToken,
          pageUrl: window.location.href
        })
      });
      if (response.ok) {
        setFormState('success');
        setEmail('');
        setCompany('');
        trackFormSubmissionGTM('newsletter', true);
      } else {
        const data = await response.json();
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
        setFormState('error');
        trackFormSubmissionGTM('newsletter', false);
      }
    } catch {
      setErrorMessage('An unexpected error occurred. Please try again.');
      setFormState('error');
      trackFormSubmissionGTM('newsletter', false);
    }
  };
  const newsletterGlowStyle = {
    background: 'radial-gradient(circle, var(--color-aq-orange) 0%, transparent 70%)'
  } as const;
  return <section className="relative overflow-hidden bg-accent py-20 text-white" aria-label="Newsletter signup" role="region">
            {/* Background Decorative Elements */}
            <Box className="absolute inset-0 z-0 bg-gradient-to-br newsletter-gradient-background" />
            <Box className="absolute -right-20 -top-20 z-0 h-96 w-96 opacity-5 blur-3xl" style={newsletterGlowStyle} />

            <BackgroundPattern type="aq-watermark" opacity={0.05} isDark={true} className="left-0 -translate-x-1/4 -rotate-12" />

            <Box className="container relative z-10 mx-auto px-4">
                <Box className="grid grid-cols-1 md:grid-cols-3 gap-12 items-center">
                    {/* Left side: Text Stack */}
                    <Box className="md:col-span-1 space-y-3">
                        <Text as="p" className="text-3xl font-bold leading-tight">
                            Sign up to receive the latest insights.
                        </Text>
                        <Text as="p" className="text-primary font-bold uppercase tracking-widest text-sm md:text-base">
                            Subscribe to our newsletter
                        </Text>
                    </Box>

                    {/* Right side: Modern Signup Form */}
                    <Box className="md:col-span-2">
                        {formState === 'success' ? <Box className="flex items-center gap-3 text-lg">
                                <CheckCircle className="w-6 h-6 text-green-400" />
                                <Text>Thank you for subscribing!</Text>
                            </Box> : <form className="flex flex-col sm:flex-row flex-wrap gap-4" onSubmit={handleSubmit} aria-label="Newsletter signup" role="form" data-hydrated={isHydrated ? "true" : undefined}>
                                <Box className="relative w-full sm:flex-1 sm:min-w-0 group">
                                    <label htmlFor="footer-company" className="sr-only">Company Name</label>
                                    <Input id="footer-company" type="text" name="company" aria-label="Company Name" placeholder="Company Name" value={company} onChange={e_0 => {
                setCompany(e_0.target.value);
                if (formState === 'error') setFormState('idle');
              }} autoComplete="organization" className="h-14 w-full rounded-xl bg-white/10 text-white placeholder:text-white/60 backdrop-blur-md border-white/20 px-6 text-lg transition-all duration-300 focus:bg-white/20 focus:border-primary/50 focus:ring-0" required disabled={formState === 'submitting'} />
                                </Box>

                                <Box className="relative w-full sm:flex-1 sm:min-w-0 group">
                                    <label htmlFor="footer-email" className="sr-only">Email Address</label>
                                    <Input id="footer-email" type="email" name="email" aria-label="Email Address" placeholder="Email Address" value={email} onChange={e_1 => {
                setEmail(e_1.target.value);
                if (formState === 'error') setFormState('idle');
              }} autoComplete="email" className="h-14 w-full rounded-xl bg-white/10 text-white placeholder:text-white/60 backdrop-blur-md border-white/20 px-6 text-lg transition-all duration-300 focus:bg-white/20 focus:border-primary/50 focus:ring-0" required disabled={formState === 'submitting'} />
                                    <Box className="absolute inset-0 rounded-xl bg-gradient-to-r from-aq-orange/20 to-aq-electric-blue/20 opacity-0 group-focus-within:opacity-100 blur-xl transition-opacity duration-500 -z-10" />
                                </Box>

                                <Button type="submit" size="lg" aria-label="Subscribe to newsletter" disabled={formState === 'submitting'} className="h-14 w-full sm:w-auto px-10 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-lg shadow-lg transition-all duration-300 active:scale-95 whitespace-nowrap">
                                    {formState === 'submitting' ? <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            Submitting...
                                        </> : 'SUBMIT'}
                                </Button>
                            </form>}
                        {formState === 'error' && errorMessage && <Text as="p" className="mt-2 text-sm text-red-300">{errorMessage}</Text>}
                        {formState !== 'success' && <Text as="p" className="mt-4 text-xs text-white/70 italic">
                                By subscribing, you agree to our privacy policy and terms of service.
                            </Text>}
                    </Box>
                </Box>
            </Box>

            {/* Bottom Border Accent */}
            <Box className="absolute bottom-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        </section>;
};